/**
 * Outreach is sent as ordinary email from the firm's own mailbox.
 *
 * No forwarding or third-party mail service is involved: the console prepares
 * the message and hands it to a real mail composer, so what the candidate
 * receives is a normal personal email from a person, with normal headers and
 * normal threading on reply.
 */

export const FIRM_EMAIL = 'hello@velnorisexecutivenetwork.com';

const SENDER_KEY = 'velnoris-outreach-sender';
const CLIENT_KEY = 'velnoris-outreach-mail-client';

export type MailClient = 'default' | 'gmail' | 'outlook';

/** The address outreach is sent from, and which replies return to. */
export function getSenderEmail(): string {
  try {
    return window.localStorage.getItem(SENDER_KEY)?.trim() || FIRM_EMAIL;
  } catch {
    return FIRM_EMAIL;
  }
}

export function setSenderEmail(address: string): void {
  try {
    window.localStorage.setItem(SENDER_KEY, address.trim());
  } catch {
    /* Storage unavailable — the address falls back to the firm mailbox. */
  }
}

/** Which composer to open: the desktop mail app, Gmail, or Outlook on the web. */
export function getMailClient(): MailClient {
  try {
    const stored = window.localStorage.getItem(CLIENT_KEY);
    return stored === 'gmail' || stored === 'outlook' ? stored : 'default';
  } catch {
    return 'default';
  }
}

export function setMailClient(client: MailClient): void {
  try {
    window.localStorage.setItem(CLIENT_KEY, client);
  } catch {
    /* Storage unavailable — the desktop mail app is used. */
  }
}

export const mailClientLabels: Record<MailClient, string> = {
  default: 'My mail app',
  gmail: 'Gmail',
  outlook: 'Outlook on the web',
};

/** Builds the compose URL for the chosen mail client. */
export function composeUrl(
  to: string,
  subject: string,
  body: string,
  client: MailClient = getMailClient(),
  sender: string = getSenderEmail(),
): string {
  const e = encodeURIComponent;
  if (client === 'gmail') {
    return `https://mail.google.com/mail/u/${e(sender)}/?view=cm&fs=1&to=${e(to)}&su=${e(subject)}&body=${e(body)}`;
  }
  if (client === 'outlook') {
    return `https://outlook.office.com/mail/deeplink/compose?to=${e(to)}&subject=${e(subject)}&body=${e(body)}`;
  }
  return `mailto:${e(to)}?subject=${e(subject)}&body=${e(body)}`;
}

/** Opens the composer. Returns false only if the browser blocked it. */
export function openCompose(
  to: string,
  subject: string,
  body: string,
  client: MailClient = getMailClient(),
): boolean {
  const url = composeUrl(to, subject, body, client);
  if (client === 'default') {
    window.location.href = url;
    return true;
  }
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  return Boolean(opened);
}
